/** Pure helpers shared by Client Portal page and extracted UI components. */

import {
  CONTRIBUTOR_WORKFLOW_ROLES,
  getPortalPersonaHeadingLabel,
  getPortalPersonaLabel,
  isClientPersonaRole,
  isContributorPersonaRole,
  isProjectAdminPersonaRole,
  pickPersonaRoleFromList,
  usesContributorProjectListCopy,
  WORKFLOW_ROLE_PERSONA_PRIORITY,
} from "../utils/workflowCapabilities";

export function safeStr(x) {
  return (x ?? "").toString();
}

export function toDateObj(v) {
  if (!v) return null;
  try {
    if (v.toDate) return v.toDate();
    if (v instanceof Date) return v;
    return null;
  } catch {
    return null;
  }
}

export function relativeTimeFrom(dateObj) {
  if (!dateObj) return "—";
  const d = dateObj instanceof Date ? dateObj : toDateObj(dateObj);
  if (!d) return "—";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function isLikelyImage(fileMeta) {
  const t = safeStr(fileMeta?.contentType || fileMeta?.type || "").toLowerCase();
  if (t.startsWith("image/")) return true;
  const name = safeStr(fileMeta?.name || "").toLowerCase();
  return /\.(png|jpg|jpeg|webp|gif|heic)$/i.test(name);
}

export function getPreviewUrl(fileMeta) {
  const f = fileMeta || {};
  return f.thumbnailUrl || f.thumbUrl || f.previewUrl || f.url || "";
}

export function fileExtFromName(name) {
  const n = safeStr(name).trim();
  const m = n.match(/\.([a-z0-9]{1,8})$/i);
  return m ? m[1].toLowerCase() : "";
}

export function fileKind(fileMeta) {
  const t = safeStr(fileMeta?.contentType || fileMeta?.type || "").toLowerCase();
  if (t.startsWith("image/") || isLikelyImage(fileMeta)) return "image";
  if (t.includes("pdf") || fileExtFromName(fileMeta?.name) === "pdf") return "pdf";
  const ext = fileExtFromName(fileMeta?.name);
  if (["doc", "docx"].includes(ext)) return "doc";
  if (["xls", "xlsx", "csv"].includes(ext)) return "sheet";
  if (["ppt", "pptx"].includes(ext)) return "slides";
  if (["dwg", "dxf"].includes(ext)) return "cad";
  if (["zip", "rar", "7z"].includes(ext)) return "archive";
  return ext ? "file" : "file";
}

export function fileBadgeLabel(fileMeta) {
  const kind = fileKind(fileMeta);
  const ext = fileExtFromName(fileMeta?.name);
  if (kind === "image") return ext ? ext.toUpperCase() : "IMG";
  if (kind === "pdf") return "PDF";
  if (kind === "cad") return ext ? ext.toUpperCase() : "CAD";
  if (kind === "doc") return ext ? ext.toUpperCase() : "DOC";
  if (kind === "sheet") return ext ? ext.toUpperCase() : "XLS";
  if (kind === "slides") return ext ? ext.toUpperCase() : "PPT";
  if (kind === "archive") return ext ? ext.toUpperCase() : "ZIP";
  return ext ? ext.toUpperCase() : "FILE";
}

export function phaseForFile(fileMeta) {
  const cat = safeStr(fileMeta?.category).toLowerCase();
  const sub = safeStr(fileMeta?.subcategory).toLowerCase();

  if (cat === "design") return "planning";
  if (cat === "client") return "construction";

  const planningKeys = ["render", "concept", "drawing", "permit", "scope", "budget", "estimate"];
  const constructionKeys = ["progress", "site", "field", "inspection", "change", "rfi", "photo"];

  if (planningKeys.some((k) => sub.includes(k))) return "planning";
  if (constructionKeys.some((k) => sub.includes(k))) return "construction";

  return "planning";
}

export function groupFilesByPhase(files) {
  const out = { planning: [], construction: [] };
  (files || []).forEach((f) => {
    const ph = phaseForFile(f);
    if (ph === "construction") out.construction.push(f);
    else out.planning.push(f);
  });
  return out;
}

export function displayNameFromProfile(profile, user) {
  const first = safeStr(profile?.firstName).trim();
  const last = safeStr(profile?.lastName).trim();
  const combined = `${first} ${last}`.trim();
  if (combined) return combined;
  if (safeStr(profile?.displayName).trim()) return safeStr(profile?.displayName).trim();
  if (safeStr(user?.email).trim()) return safeStr(user?.email).trim();
  return "Client";
}

/** First name (or best-effort) for portal headings — avoids showing last name on screen. */
export function portalHeadingFirstName(profile, user) {
  const first = safeStr(profile?.firstName).trim();
  if (first) return first;
  const display = safeStr(profile?.displayName).trim();
  if (display) {
    const word = display.split(/\s+/)[0]?.replace(/[,;]$/, "") || "";
    if (word) return word;
  }
  const email = safeStr(user?.email).trim();
  if (email) {
    const local = (email.split("@")[0] || "").trim();
    const seg = local.split(/[._]/)[0] || local;
    if (seg.length >= 2 && /^[a-zA-Z]+$/.test(seg)) {
      return seg.charAt(0).toUpperCase() + seg.slice(1).toLowerCase();
    }
    if (seg) return seg;
  }
  return "";
}

/** English possessive for headings: Jill → Jill's, Chris → Chris'. */
export function englishPossessive(name) {
  const s = safeStr(name).trim();
  if (!s) return "";
  return /s$/i.test(s) ? `${s}'` : `${s}'s`;
}

function uniqueStrings(values) {
  return Array.from(
    new Set((Array.isArray(values) ? values : []).map((value) => safeStr(value).trim().toLowerCase()).filter(Boolean))
  );
}

/** Entitlement / auth bucket keys — not portal persona labels (CL-338F). */
const PORTAL_ACCESS_TYPE_KEYS = new Set([
  "client_portal",
  "construction",
  "construction_collaborator",
  "real_estate",
  "realestate",
  "admin",
  "guest",
]);

/**
 * Persona-bearing roles from profile only (primaryUserType + constructionRoles + selective users.role).
 * Does not use allowedUserTypes — avoids client_portal / stale "client" polluting the header.
 */
export function personaRolesFromProfile(profile) {
  const primary = safeStr(profile?.primaryUserType).toLowerCase();
  const fromConstruction = Array.isArray(profile?.constructionRoles) ? profile.constructionRoles : [];
  const authRole = safeStr(profile?.role).toLowerCase();
  const values = [primary, ...fromConstruction];
  if (
    authRole === "client" ||
    authRole === "admin" ||
    isProjectAdminPersonaRole(authRole) ||
    isContributorPersonaRole(authRole)
  ) {
    values.push(authRole);
  }
  return uniqueStrings(values.filter((r) => r && !PORTAL_ACCESS_TYPE_KEYS.has(r)));
}

/**
 * Pick one canonical persona key from role strings (roster, assignment, or profile).
 * @returns {string}
 */
export function pickPersonaFromRoles(roleList) {
  return pickPersonaRoleFromList(roleList);
}

function personaFromPrimaryUserType(profile) {
  const primary = safeStr(profile?.primaryUserType).toLowerCase();
  if (!primary || PORTAL_ACCESS_TYPE_KEYS.has(primary)) return null;
  if (isProjectAdminPersonaRole(primary)) return "project_admin";
  if (isClientPersonaRole(primary)) return "client";
  if (isContributorPersonaRole(primary)) return primary;
  return null;
}

/**
 * Canonical portal persona for shell header / home copy (profile-level). CL-338F
 * @returns {string} guest | admin | project_admin | client | designer | engineer | …
 */
export function resolvePortalPersonaFromProfile(profile, { isAnonymous } = {}) {
  if (isAnonymous) return "guest";
  if (!profile) return "guest";

  const authRole = safeStr(profile.role).toLowerCase();
  if (authRole === "admin" && profile.projectAdminEquivalent === true) return "project_admin";
  if (authRole === "admin") return "admin";

  const fromPrimary = personaFromPrimaryUserType(profile);
  if (fromPrimary) return fromPrimary;

  return pickPersonaFromRoles(personaRolesFromProfile(profile));
}

/**
 * Shell header persona: active project roster/assignment wins over profile (CL-366B).
 */
export function resolvePortalPersonaForShell({
  profile,
  project,
  matchedCollaborator,
  viewer,
  isAnonymous,
} = {}) {
  if (project?.id || matchedCollaborator) {
    return resolvePortalPersona({
      profile,
      project,
      matchedCollaborator,
      viewer,
      isAnonymous,
    });
  }
  return resolvePortalPersonaFromProfile(profile, { isAnonymous });
}

/**
 * Canonical persona with project roster / assignment when available. CL-338F
 */
export function resolvePortalPersona({
  profile,
  project,
  matchedCollaborator,
  viewer,
  isAnonymous,
} = {}) {
  if (isAnonymous || viewer?.isAnonymous) return "guest";

  const authRole = safeStr(profile?.role).toLowerCase();
  if (authRole === "admin" && profile?.projectAdminEquivalent !== true) return "admin";

  const rowRole = safeStr(matchedCollaborator?.role).toLowerCase();
  const assignmentRole = safeStr(project?.portalRole).toLowerCase();
  const projectRole = rowRole || assignmentRole;
  if (projectRole) {
    if (isProjectAdminPersonaRole(projectRole)) return "project_admin";
    if (isClientPersonaRole(projectRole)) return "client";
    if (isContributorPersonaRole(projectRole)) return projectRole;
  }

  const cuid = safeStr(project?.clientUid || "").trim();
  const vuid = safeStr(viewer?.uid || "").trim();
  if (cuid && vuid && cuid === vuid) return "client";

  return resolvePortalPersonaFromProfile(profile, { isAnonymous: !!isAnonymous });
}

/** All-caps header sublabel for a canonical persona key. */
export function portalShellSublabelFromPersona(persona) {
  return getPortalPersonaLabel(persona);
}

/** CL-374A: real-estate workspace shell sublabel (not a workflow persona). */
export const REAL_ESTATE_SHELL_SUBLABEL = "REAL ESTATE PORTAL";

function isClientPortalProjectShellPath(pathname) {
  const path = safeStr(pathname);
  return path.startsWith("/client-portal/projects") || path.startsWith("/client-portal/markup");
}

function personaHeadingLabelWord(persona) {
  return getPortalPersonaHeadingLabel(persona);
}

/**
 * @deprecated Prefer resolvePortalPersonaFromProfile — kept for legacy kind checks.
 */
export function effectivePortalRolesNormalized(profile) {
  return personaRolesFromProfile(profile);
}

/** @deprecated Import from `workflowCapabilities` — re-export for existing imports. */
export const CONTRIBUTOR_PERSONA_ROLES = CONTRIBUTOR_WORKFLOW_ROLES;

export { isClientPersonaRole, isContributorPersonaRole, isProjectAdminPersonaRole };

/** @deprecated Use WORKFLOW_ROLE_PERSONA_PRIORITY from workflowCapabilities. */
export const PERSONA_PRIORITY = WORKFLOW_ROLE_PERSONA_PRIORITY;

/** CL-338C: global or per-project project administrator (admin redirect handles global admin). */
export function isProjectAdminPersona(profile) {
  const authRole = safeStr(profile?.role).toLowerCase();
  if (authRole === "admin" && profile?.projectAdminEquivalent === true) return true;
  return personaRolesFromProfile(profile).includes("project_admin");
}

/**
 * Contributor-style project list / action copy — driven by role/persona, not accessScope alone.
 * `shared_files` limits which files load; it must not imply designer portal messaging for clients.
 */
export function shouldUseContributorProjectListCopy({ profile, project, isAnonymous } = {}) {
  if (isAnonymous) return false;
  const portalRole = safeStr(project?.portalRole).trim().toLowerCase();
  if (usesContributorProjectListCopy(portalRole)) return true;
  const persona = project
    ? resolvePortalPersona({ profile, project, isAnonymous })
    : resolvePortalPersonaFromProfile(profile, { isAnonymous });
  return usesContributorProjectListCopy(persona);
}

/**
 * Per-project viewer persona for list/drawer/review UX (CL-338D / CL-338F).
 * @returns {"guest"|"admin"|"client"|"contributor"|"project_admin"}
 */
export function resolveProjectViewerPersona({ viewer, viewerProfile, project, matchedCollaborator }) {
  const persona = resolvePortalPersona({
    profile: viewerProfile,
    project,
    matchedCollaborator,
    viewer,
    isAnonymous: !!viewer?.isAnonymous,
  });
  if (persona === "guest") return "guest";
  if (persona === "admin") return "admin";
  if (persona === "project_admin") return "project_admin";
  if (persona === "client") return "client";
  return "contributor";
}

/**
 * Legacy workspace “kind” for a few grouped UI branches (prefer resolvePortalPersona*).
 * @returns {"client"|"designer"|"trade"|"project_admin"|"guest"}
 */
export function portalWorkspaceKind(profile, { isAnonymous } = {}) {
  const persona = resolvePortalPersonaFromProfile(profile, { isAnonymous });
  if (persona === "guest") return "guest";
  if (persona === "admin") return "client";
  if (persona === "project_admin") return "project_admin";
  if (persona === "client") return "client";
  if (persona === "designer" || persona === "engineer") return "designer";
  if (isContributorPersonaRole(persona)) return "trade";
  return "client";
}

/** All-caps sublabel under the brand in the shared portal header. */
export function portalShellSublabelFromProfile(profile, { isAnonymous } = {}) {
  return portalShellSublabelFromPersona(resolvePortalPersonaFromProfile(profile, { isAnonymous }));
}

/**
 * Role-aware shell sublabel; prefers active project context when provided (CL-366B).
 * CL-374A: real-estate routes use REAL ESTATE PORTAL unless viewing a project shell path.
 */
export function portalShellSublabelFromShellContext({
  profile,
  project,
  matchedCollaborator,
  viewer,
  isAnonymous,
  pathname,
} = {}) {
  const path = safeStr(pathname);
  if (!isClientPortalProjectShellPath(path) && path.startsWith("/client-portal/real-estate")) {
    return REAL_ESTATE_SHELL_SUBLABEL;
  }
  return portalShellSublabelFromPersona(
    resolvePortalPersonaForShell({
      profile,
      project,
      matchedCollaborator,
      viewer,
      isAnonymous,
    })
  );
}

/**
 * Main H1 on the portal home (sentence-style “… portal”).
 * @param {object|null|undefined} profile
 * @param {import('firebase/auth').User|null|undefined} user
 * @param {{ isAnonymous?: boolean }} [options]
 */
export function buildPortalWorkspaceHeadingTitle(profile, user, { isAnonymous } = {}) {
  if (isAnonymous) return "Guest portal";
  const persona = resolvePortalPersonaFromProfile(profile, { isAnonymous });
  const first = portalHeadingFirstName(profile, user);
  const labelWord = personaHeadingLabelWord(persona);
  if (first) return `${englishPossessive(first)} ${labelWord} portal`;
  return `Your ${labelWord} portal`;
}

/**
 * Full H1 line for the portal landing (role-aware).
 * @param {object|null|undefined} profile
 * @param {import('firebase/auth').User|null|undefined} user
 * @param {{ isAnonymous?: boolean }} [options]
 */
export function buildClientPortalHeadingTitle(profile, user, options = {}) {
  return buildPortalWorkspaceHeadingTitle(profile, user, options);
}
